import React, { createContext, useContext, useMemo } from 'react';
import {
  useOpsCommandLive,
  useOpsGuestReviews,
  useOpsNorthStar,
  useOpsPerformance,
  useOpsPerformanceHeroNuggets,
  useOpsSituationPack,
  type SourceFilter,
} from '../../../hooks/useOpsCommand';
import type {
  ConnectedSourceId,
  GuestReviewsPayload,
  HeroNugget,
  NorthStarPayload,
  OpsCommandLivePayload,
  OpsCommandPerformancePayload,
  PerfSettings,
  ReservationStatusGroup,
  ShiftFilter,
} from '../../../types';
import type { SituationPackPayload } from '../../../lib/situationTypes';
import type { HorizonPayload } from '../../horizon/horizonTypes';
import { useHorizon } from '../../horizon/useHorizon';
import { isCurrentSWRData, isFailedSWREmpty } from '../../../../../lib/hooks';
import type { CanonicalWidgetType, WidgetFamily } from './widgetTypes';

export type WidgetNeeds = {
  performance: boolean;
  live: boolean;
  heroNuggets: boolean;
  situation: boolean;
  northStar: boolean;
  guestReviews: boolean;
  horizon: boolean;
};

export function widgetNeedsFromTypes(types: Iterable<string>): WidgetNeeds {
  const needs: WidgetNeeds = {
    performance: false,
    live: false,
    heroNuggets: false,
    situation: false,
    northStar: false,
    guestReviews: false,
    horizon: false,
  };
  for (const type of types) {
    if (type === 'perf.highlightsCard') {
      needs.performance = true;
      needs.heroNuggets = true;
    } else if (type === 'perf.situation') {
      needs.performance = true;
      needs.situation = true;
    } else if (type === 'perf.northStar') {
      needs.performance = true;
      needs.northStar = true;
    } else if (type === 'perf.outletTable') {
      needs.performance = true;
      needs.guestReviews = true;
    } else if (type.startsWith('perf.')) {
      needs.performance = true;
    } else if (type.startsWith('live.')) {
      needs.live = true;
    } else if (type.startsWith('horizon.')) {
      needs.horizon = true;
    }
  }
  return needs;
}

export type AssistantWidgetData = {
  restaurantId: string;
  regionFilter: string | null;
  shifts?: ShiftFilter | null;
  perfSettings: PerfSettings;
  performance?: OpsCommandPerformancePayload;
  performanceLoading: boolean;
  heroNuggets?: HeroNugget[];
  heroNuggetsLoading: boolean;
  situationPack?: SituationPackPayload;
  situationLoading: boolean;
  northStar?: NorthStarPayload | null;
  live?: OpsCommandLivePayload;
  liveLoading: boolean;
  guestReviews?: GuestReviewsPayload;
  guestReviewsLoading: boolean;
  horizon?: HorizonPayload;
  horizonLoading: boolean;
  connected: ConnectedSourceId[];
};

const WidgetDataContext = createContext<AssistantWidgetData | null>(null);

export function useAssistantWidgetData(): AssistantWidgetData | null {
  return useContext(WidgetDataContext);
}

export function AssistantWidgetDataProvider({
  restaurantId,
  regionFilter,
  perfSettings,
  source = null,
  status = null,
  shifts = null,
  connected: connectedProp = [],
  needs,
  children,
}: {
  restaurantId: string;
  regionFilter: string | null;
  perfSettings: PerfSettings;
  source?: SourceFilter | null;
  status?: ReservationStatusGroup[] | null;
  shifts?: ShiftFilter | null;
  connected?: ConnectedSourceId[];
  needs: WidgetNeeds;
  children: React.ReactNode;
}) {
  const performance = useOpsPerformance(
    restaurantId,
    perfSettings,
    regionFilter,
    needs.performance,
    source,
    status,
    shifts,
  );
  const scorecardCurrent = isCurrentSWRData(performance);
  const scorecardFailedEmpty = isFailedSWREmpty(performance);
  const heroNuggets = useOpsPerformanceHeroNuggets(
    restaurantId,
    perfSettings,
    regionFilter,
    false,
    source,
    status,
    shifts,
  );
  const situation = useOpsSituationPack(
    restaurantId,
    'performance',
    perfSettings,
    regionFilter,
    needs.situation,
    source,
    status,
    shifts,
  );
  const northStar = useOpsNorthStar(
    restaurantId,
    perfSettings,
    regionFilter,
    needs.northStar &&
      (!needs.performance || scorecardCurrent || scorecardFailedEmpty),
    source,
    status,
    shifts,
  );
  const guestReviews = useOpsGuestReviews(
    restaurantId,
    perfSettings,
    regionFilter,
    needs.guestReviews,
  );
  const live = useOpsCommandLive(
    restaurantId,
    regionFilter,
    source,
    status,
    perfSettings.capacityBasis ?? null,
    needs.live,
    shifts,
  );
  const horizon = useHorizon(restaurantId, regionFilter, needs.horizon, shifts);

  const connected = (live.data?.connected?.length
    ? live.data.connected
    : connectedProp) as ConnectedSourceId[];

  const value = useMemo<AssistantWidgetData>(
    () => ({
      restaurantId,
      regionFilter,
      shifts,
      perfSettings,
      performance: performance.data,
      performanceLoading: !!performance.isLoading && !performance.data,
      heroNuggets: heroNuggets.data?.heroNuggets,
      heroNuggetsLoading: !!heroNuggets.isLoading && !heroNuggets.data,
      situationPack: situation.data,
      situationLoading: !!situation.isLoading && !situation.data,
      northStar: northStar.data ?? null,
      live: live.data,
      liveLoading: !!live.isLoading && !live.data,
      guestReviews: guestReviews.data,
      guestReviewsLoading: !!guestReviews.isLoading && !guestReviews.data,
      horizon: horizon.data,
      horizonLoading: !!horizon.isLoading && !horizon.data,
      connected,
    }),
    [
      restaurantId,
      regionFilter,
      shifts,
      perfSettings,
      performance.data,
      performance.isLoading,
      heroNuggets.data,
      heroNuggets.isLoading,
      situation.data,
      situation.isLoading,
      northStar.data,
      live.data,
      live.isLoading,
      guestReviews.data,
      guestReviews.isLoading,
      horizon.data,
      horizon.isLoading,
      connected,
    ],
  );

  return (
    <WidgetDataContext.Provider value={value}>
      {children}
    </WidgetDataContext.Provider>
  );
}

export function collectWidgetTypes(
  families: Array<{ widgets?: Array<{ type: string }> | undefined }>,
): CanonicalWidgetType[] {
  const types: CanonicalWidgetType[] = [];
  for (const item of families) {
    for (const widget of item.widgets ?? []) {
      types.push(widget.type as CanonicalWidgetType);
    }
  }
  return types;
}

export function familyNeeded(types: string[], family: WidgetFamily): boolean {
  return types.some(type => {
    if (type.startsWith('perf.') && family === 'performance') return true;
    if (type.startsWith('live.') && family === 'live') return true;
    if (type.startsWith('horizon.') && family === 'horizon') return true;
    return false;
  });
}
